import axios from "axios";
import type {
  User, Project, Dwelling, Room, Geometry, Stitch, Validation, ExportJob, Scan,
  SyncSession, DeviceSummary,
} from "./types";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

// Auth uses httpOnly cookies set by the backend. The browser stores and sends them
// automatically; withCredentials ensures cookies travel with every API request.
export const http = axios.create({ baseURL: API_BASE, withCredentials: true });

http.interceptors.response.use(
  (r) => r,
  (error) => {
    if (error.response?.status === 401 && !window.location.pathname.includes("/login")) {
      window.location.href = "/login";
    }
    return Promise.reject(error);
  }
);

export function formatApiError(detail: any): string {
  if (detail == null) return "Une erreur est survenue. Réessayez.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

// ---- Auth ----
export const api = {
  async register(email: string, password: string, full_name: string) {
    const { data } = await http.post("/auth/register", { email, password, full_name });
    return data;
  },
  async login(email: string, password: string) {
    const { data } = await http.post("/auth/login", { email, password });
    return data;
  },
  async logout() {
    try { await http.post("/auth/logout"); } catch (_) {}
  },
  async me(): Promise<User> {
    const { data } = await http.get("/auth/me");
    return data;
  },

  // ---- Projects ----
  async listProjects(): Promise<Project[]> {
    return (await http.get("/projects")).data;
  },
  async createProject(payload: Partial<Project>): Promise<Project> {
    return (await http.post("/projects", payload)).data;
  },
  async getProject(id: string): Promise<Project> {
    return (await http.get(`/projects/${id}`)).data;
  },
  async updateProject(id: string, payload: Partial<Project>): Promise<Project> {
    return (await http.patch(`/projects/${id}`, payload)).data;
  },

  // ---- Dwellings ----
  async createDwelling(projectId: string, payload: { name: string; levels_count: number }): Promise<Dwelling> {
    return (await http.post(`/projects/${projectId}/dwellings`, payload)).data;
  },
  async listDwellings(projectId: string): Promise<Dwelling[]> {
    return (await http.get(`/projects/${projectId}/dwellings`)).data;
  },
  async getDwelling(id: string): Promise<Dwelling> {
    return (await http.get(`/dwellings/${id}`)).data;
  },
  async updateDwelling(id: string, payload: Partial<Dwelling>): Promise<Dwelling> {
    return (await http.patch(`/dwellings/${id}`, payload)).data;
  },

  // ---- Rooms ----
  async createRoom(dwellingId: string, payload: { name: string; room_type: string; floor_index: number }): Promise<Room> {
    return (await http.post(`/dwellings/${dwellingId}/rooms`, payload)).data;
  },
  async listRooms(dwellingId: string): Promise<Room[]> {
    return (await http.get(`/dwellings/${dwellingId}/rooms`)).data;
  },
  async listRoomsFull(dwellingId: string): Promise<(Room & { geometry: Geometry | null })[]> {
    return (await http.get(`/dwellings/${dwellingId}/rooms-full`)).data;
  },
  async getRoom(id: string): Promise<Room> {
    return (await http.get(`/rooms/${id}`)).data;
  },
  async updateRoom(id: string, payload: Partial<Room>): Promise<Room> {
    return (await http.patch(`/rooms/${id}`, payload)).data;
  },

  // ---- Scans ----
  async startScan(roomId: string, device_info: any, capture_mode = "arcore"): Promise<Scan> {
    return (await http.post(`/rooms/${roomId}/scans/start`, { device_info, capture_mode })).data;
  },
  async completeScan(roomId: string, scanId: string): Promise<Scan> {
    return (await http.post(`/rooms/${roomId}/scans/${scanId}/complete`)).data;
  },
  async uploadMetadata(roomId: string, scanId: string, metadata: any) {
    return (await http.post(`/rooms/${roomId}/scans/${scanId}/upload-metadata`, { metadata })).data;
  },
  async uploadFrames(roomId: string, scanId: string, files: File[]): Promise<Scan> {
    const fd = new FormData();
    files.forEach((f) => fd.append("files", f));
    return (await http.post(`/rooms/${roomId}/scans/${scanId}/upload-frames`, fd)).data;
  },
  async listScans(roomId: string): Promise<Scan[]> {
    return (await http.get(`/rooms/${roomId}/scans`)).data;
  },

  // ---- Geometry ----
  async reconstruct(roomId: string): Promise<Geometry> {
    return (await http.post(`/rooms/${roomId}/reconstruct`)).data;
  },
  async latestGeometry(roomId: string): Promise<Geometry> {
    return (await http.get(`/rooms/${roomId}/geometry/latest`)).data;
  },
  async manualEdit(roomId: string, payload: any): Promise<Geometry> {
    return (await http.post(`/rooms/${roomId}/geometry/manual-edit`, payload)).data;
  },
  async metrePdf(roomId: string): Promise<Blob> {
    return (await http.get(`/rooms/${roomId}/metre.pdf`, { responseType: "blob" })).data;
  },
  async planPdf(roomId: string): Promise<Blob> {
    return (await http.get(`/rooms/${roomId}/plan.pdf`, { responseType: "blob" })).data;
  },
  async dwellingPlanPdf(dwellingId: string): Promise<Blob> {
    return (await http.get(`/dwellings/${dwellingId}/plan.pdf`, { responseType: "blob" })).data;
  },
  async dossierCarrezPdf(dwellingId: string): Promise<Blob> {
    return (await http.get(`/dwellings/${dwellingId}/dossier-carrez.pdf`, { responseType: "blob" })).data;
  },

  // ---- Stitches ----
  async createStitch(dwellingId: string) {
    return (await http.post(`/dwellings/${dwellingId}/stitch`)).data;
  },
  async listStitches(dwellingId: string): Promise<Stitch[]> {
    return (await http.get(`/dwellings/${dwellingId}/stitches`)).data;
  },
  async validateStitch(stitchId: string, status: string): Promise<Stitch> {
    return (await http.patch(`/stitches/${stitchId}/validate`, { status })).data;
  },
  async correctStitch(stitchId: string, transform: any): Promise<Stitch> {
    return (await http.patch(`/stitches/${stitchId}/correct`, { transform })).data;
  },

  // ---- Validations ----
  async listValidations(projectId: string): Promise<Validation[]> {
    return (await http.get(`/projects/${projectId}/validations`)).data;
  },
  async updateValidation(id: string, status: string): Promise<Validation> {
    return (await http.patch(`/validations/${id}`, { status })).data;
  },
  async runValidation(dwellingId: string): Promise<{ validation_status: string; issues: Validation[]; ai_summary: string }> {
    return (await http.post(`/dwellings/${dwellingId}/run-validation`)).data;
  },

  // ---- Exports ----
  async createExport(dwellingId: string, format: string, force = false): Promise<ExportJob> {
    return (await http.post(`/dwellings/${dwellingId}/exports`, { format, force })).data;
  },
  async listExports(dwellingId: string): Promise<ExportJob[]> {
    return (await http.get(`/dwellings/${dwellingId}/exports`)).data;
  },
  async getExport(id: string): Promise<ExportJob> {
    return (await http.get(`/exports/${id}`)).data;
  },

  // ---- Sync journal (diagnostics) ----
  async syncSessions(deviceId?: string, limit = 50): Promise<SyncSession[]> {
    const params: any = { limit };
    if (deviceId) params.device_id = deviceId;
    return (await http.get(`/sync/sessions`, { params })).data;
  },
  async syncSummary(): Promise<{ devices: DeviceSummary[]; total_sessions: number }> {
    return (await http.get(`/sync/sessions/summary`)).data;
  },
};
